import type { Vehicle } from '../types/vehicle'

export interface SelectorState {
  q: string
  precio: string
  tipos: string[]
  prefs: string[]
  anioDesde: string
  anioHasta: string
  kmMax: string
  combustible: string
  transmision: string
}

export const initialSelector: SelectorState = {
  q: '',
  precio: '',
  tipos: [],
  prefs: [],
  anioDesde: '',
  anioHasta: '',
  kmMax: '',
  combustible: '',
  transmision: '',
}

export const LOW_KM = 60000

export const PRICE_RANGES: {
  id: string
  label: string
  min: number
  max: number | null
}[] = [
  { id: 'hasta-10k', label: 'Hasta USD 10.000', min: 0, max: 10000 },
  { id: '10k-20k', label: 'USD 10.000 a USD 20.000', min: 10000, max: 20000 },
  { id: '20k-35k', label: 'USD 20.000 a USD 35.000', min: 20000, max: 35000 },
  { id: 'mas-35k', label: 'Más de USD 35.000', min: 35000, max: null },
]

export const TIPO_OPTIONS: {
  id: string
  tipos: string[]
  keywords?: string[]
}[] = [
  { id: 'Hatchback', tipos: ['Hatchback'] },
  { id: 'Sedán', tipos: ['Sedán'] },
  { id: 'SUV', tipos: ['SUV'] },
  { id: 'Pick-up', tipos: ['Pick-up'] },
  { id: 'Deportivo', tipos: ['Coupé'], keywords: ['gli', 'gt', ' st', 'sport'] },
  { id: 'Familiar', tipos: ['SUV', 'Sedán', 'Pick-up'] },
]

export const PREF_OPTIONS = [
  'Con financiación',
  'Acepta permuta',
  'Oportunidad',
  'Bajo kilometraje',
  'Automático',
  'Manual',
] as const

export const ANIO_DESDE_OPTIONS = ['2015', '2018', '2020', '2022', '2024']
export const ANIO_HASTA_OPTIONS = ['2019', '2021', '2023', '2026']

export const KM_OPTIONS: { id: string; label: string }[] = [
  { id: '30000', label: 'Hasta 30.000 km' },
  { id: '50000', label: 'Hasta 50.000 km' },
  { id: '80000', label: 'Hasta 80.000 km' },
  { id: '120000', label: 'Hasta 120.000 km' },
  { id: '', label: 'Sin límite' },
]

export const COMBUSTIBLE_OPTIONS = ['Nafta', 'Diesel', 'Híbrido', 'Eléctrico']
export const TRANSMISION_OPTIONS = ['Manual', 'Automática']

function matchesTipo(v: Vehicle, tipos: string[]): boolean {
  if (!tipos.length) return true
  return tipos.some((id) => {
    const option = TIPO_OPTIONS.find((t) => t.id === id)
    if (!option) return false
    if (option.tipos.includes(v.tipo)) return true
    const haystack = `${v.version} ${v.descripcionCorta}`.toLowerCase()
    return (option.keywords ?? []).some((k) => haystack.includes(k))
  })
}

function matchesText(v: Vehicle, q: string): boolean {
  const term = q.trim().toLowerCase()
  if (!term) return true
  const haystack = [
    v.marca,
    v.modelo,
    v.version,
    v.descripcionCorta,
    v.descripcionCompleta,
  ]
    .join(' ')
    .toLowerCase()
  return term
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token))
}

export function matchesSelector(v: Vehicle, s: SelectorState): boolean {
  if (v.estado !== 'Usado' || !v.disponible) return false
  if (!matchesText(v, s.q)) return false
  if (!matchesTipo(v, s.tipos)) return false

  const range = PRICE_RANGES.find((r) => r.id === s.precio)
  if (range) {
    if (v.precio < range.min) return false
    if (range.max !== null && v.precio > range.max) return false
  }

  if (s.anioDesde && v.anio < Number(s.anioDesde)) return false
  if (s.anioHasta && v.anio > Number(s.anioHasta)) return false
  if (s.kmMax && v.kilometraje > Number(s.kmMax)) return false
  if (s.combustible && v.combustible !== s.combustible) return false
  if (s.transmision && v.transmision !== s.transmision) return false

  if (s.prefs.includes('Con financiación') && !v.financiacion) return false
  if (s.prefs.includes('Acepta permuta') && !v.permuta) return false
  if (s.prefs.includes('Oportunidad') && !v.oportunidad) return false
  if (s.prefs.includes('Bajo kilometraje') && v.kilometraje > LOW_KM) return false
  if (s.prefs.includes('Automático') && v.transmision !== 'Automática') return false
  if (s.prefs.includes('Manual') && v.transmision !== 'Manual') return false

  return true
}

function score(v: Vehicle, s: SelectorState): number {
  let total = 0
  if (v.destacado) total += 4
  if (v.oportunidad) total += 3
  if (v.financiacion) total += 1
  if (v.permuta) total += 1
  if (v.kilometraje <= LOW_KM) total += 1
  if (s.tipos.length && matchesTipo(v, s.tipos)) total += 1
  return total
}

export function selectorResults(vehicles: Vehicle[], s: SelectorState): Vehicle[] {
  return vehicles
    .filter((v) => matchesSelector(v, s))
    .sort((a, b) => score(b, s) - score(a, s) || a.precio - b.precio)
}

/** Maps the guided selector onto the catalogue URL so results carry over. */
export function selectorCatalogQuery(s: SelectorState): string {
  const params = new URLSearchParams()
  params.set('estado', 'Usado')
  if (s.q.trim()) params.set('q', s.q.trim())
  const range = PRICE_RANGES.find((r) => r.id === s.precio)
  if (range) {
    if (range.min) params.set('precioMin', String(range.min))
    if (range.max !== null) params.set('precioMax', String(range.max))
  }
  const tipo = s.tipos.length === 1 ? TIPO_OPTIONS.find((t) => t.id === s.tipos[0]) : undefined
  if (tipo && tipo.tipos.length === 1) params.set('tipo', tipo.tipos[0])
  if (s.anioDesde) params.set('anioDesde', s.anioDesde)
  if (s.anioHasta) params.set('anioHasta', s.anioHasta)
  const km = s.kmMax || (s.prefs.includes('Bajo kilometraje') ? String(LOW_KM) : '')
  if (km) params.set('kmMax', km)
  if (s.combustible) params.set('combustible', s.combustible)
  const transmision =
    s.transmision ||
    (s.prefs.includes('Automático')
      ? 'Automática'
      : s.prefs.includes('Manual')
        ? 'Manual'
        : '')
  if (transmision) params.set('transmision', transmision)
  if (s.prefs.includes('Con financiación')) params.set('financiacion', '1')
  if (s.prefs.includes('Acepta permuta')) params.set('permuta', '1')
  if (s.prefs.includes('Oportunidad')) params.set('oportunidad', '1')
  return `/catalogo?${params.toString()}`
}

export const SELECTOR_WA_MESSAGE =
  'Hola VCARS, estoy buscando un usado seleccionado con estas características. ¿Me pueden asesorar?'
